import React, { useState } from "react";
import { createUserWithEmailAndPassword } from "firebase/auth";
import { doc, setDoc } from "firebase/firestore";
import { auth, db } from "../firebase";
import { validateKvkNumber } from "../services/kvkService";
import { sendShopConfirmationEmail } from "../services/emailService";

const OpenShop = ({ onBack, onSuccess }) => {
  const [formData, setFormData] = useState({
    shopName: "",
    companyName: "",
    kvkNumber: "",
    address: "",
    postalCode: "",
    city: "",
    phone: "",
    website: "",
    email: "",
    password: "",
    confirmPassword: ""
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [kvkValidation, setKvkValidation] = useState(null);
  const [isValidatingKvk, setIsValidatingKvk] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear KVK validation when KVK number changes
    if (name === 'kvkNumber') {
      setKvkValidation(null);
    }
  };

  const validateKvk = async () => {
    if (!formData.kvkNumber.trim()) {
      setError("Vul eerst een KVK nummer in");
      return false;
    }

    setIsValidatingKvk(true);
    setError("");

    try {
      const result = await validateKvkNumber(formData.kvkNumber);
      
      if (result.isValid) {
        setKvkValidation(result);
        return true;
      } else {
        setError(result.error);
        return false;
      }
    } catch (error) {
      console.error('KVK validation error:', error);
      setError("Fout bij het controleren van KVK nummer. Controleer het nummer en probeer het opnieuw.");
      return false;
    } finally {
      setIsValidatingKvk(false);
    }
  };

  const validateForm = () => {
    if (!formData.shopName.trim()) {
      setError("Vul een winkelnaam in");
      return false;
    }

    if (!formData.companyName.trim()) {
      setError("Vul een bedrijfsnaam in");
      return false;
    }

    if (!formData.kvkNumber.trim()) {
      setError("Vul een KVK nummer in");
      return false;
    }

    if (!formData.address.trim()) {
      setError("Vul een adres in");
      return false;
    }

    if (!formData.postalCode.trim()) {
      setError("Vul een postcode in");
      return false;
    }

    if (!formData.city.trim()) {
      setError("Vul een plaats in");
      return false;
    }

    if (!formData.phone.trim()) {
      setError("Vul een telefoonnummer in");
      return false;
    }

    if (!formData.website.trim()) {
      setError("Vul een website in");
      return false;
    }

    if (!formData.email.trim()) {
      setError("Vul een email adres in");
      return false;
    }

    if (!formData.password) {
      setError("Vul een wachtwoord in");
      return false;
    }

    if (formData.password !== formData.confirmPassword) {
      setError("Wachtwoorden komen niet overeen");
      return false;
    }

    if (formData.password.length < 6) {
      setError("Wachtwoord moet minimaal 6 karakters bevatten");
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    // Validate all fields
    if (!validateForm()) {
      setLoading(false);
      return;
    }

    // Validate KVK number
    const isKvkValid = await validateKvk();
    if (!isKvkValid) {
      setLoading(false);
      return;
    }

    try {
      // Create user account
      const userCredential = await createUserWithEmailAndPassword(auth, formData.email, formData.password);
      const user = userCredential.user;
      
      // Create shop document
      const shopId = user.uid;
      const composedLocation = [formData.address, formData.postalCode, formData.city]
        .filter(Boolean)
        .join(', ');
      const shopData = {
        name: formData.shopName,
        companyName: formData.companyName,
        kvkNumber: kvkValidation.kvkNumber,
        address: formData.address,
        postalCode: formData.postalCode,
        city: formData.city,
        location: composedLocation,
        phone: formData.phone,
        website: formData.website,
        email: formData.email,
        createdAt: new Date(),
        // Nieuwe shops zijn in afwachting van goedkeuring
        isActive: false,
        isVerified: false
      };
      
      await setDoc(doc(db, "shops", shopId), shopData);
      
      // Create user document (nog geen toegang tot beheer tot goedkeuring)
      await setDoc(doc(db, "users", user.uid), {
        email: formData.email,
        firstName: formData.shopName,
        lastName: "",
        isAdmin: false,
        adminShopId: null,
        createdAt: new Date()
      });
      
      // Send confirmation email
      await sendShopConfirmationEmail(shopData, formData.email);
      
      onSuccess();
      
    } catch (error) {
      console.error("Shop creation error:", error);
      
      if (error.code === 'auth/email-already-in-use') {
        setError("Dit email adres is al in gebruik. Gebruik een ander email adres of log in met je bestaande account.");
      } else if (error.code === 'auth/weak-password') {
        setError("Wachtwoord is te zwak. Gebruik minimaal 6 karakters.");
      } else if (error.code === 'auth/invalid-email') {
        setError("Ongeldig email adres.");
      } else {
        setError("Er is een fout opgetreden bij het aanmaken van je winkel. Probeer het opnieuw.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      minHeight: "100vh",
      background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
    }}>
      <div style={{
        background: "white",
        padding: "40px",
        borderRadius: "10px",
        boxShadow: "0 10px 25px rgba(0,0,0,0.1)",
        width: "100%",
        maxWidth: "600px"
      }}>
        <h1 style={{
          textAlign: "center",
          marginBottom: "30px",
          color: "#333",
          fontSize: "28px"
        }}>
          Winkel Openen
        </h1>
        
        <p style={{
          textAlign: "center",
          marginBottom: "30px",
          color: "#666",
          fontSize: "16px",
          lineHeight: "1.5"
        }}>
          Open je eigen winkel op het Cupdate platform. 
          Vul alle gegevens in om je winkel te registreren.
        </p>
        
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: "20px" }}>
            <label style={{
              display: "block",
              marginBottom: "8px",
              fontWeight: "bold",
              color: "#555"
            }}>
              Winkelnaam: *
            </label>
            <input
              type="text"
              name="shopName"
              value={formData.shopName}
              onChange={handleInputChange}
              required
              style={{
                width: "100%",
                padding: "12px",
                border: "1px solid #ddd",
                borderRadius: "5px",
                fontSize: "16px",
                boxSizing: "border-box"
              }}
              placeholder="Mijn Winkel"
            />
          </div>

          <div style={{ marginBottom: "20px" }}>
            <label style={{
              display: "block",
              marginBottom: "8px",
              fontWeight: "bold",
              color: "#555"
            }}>
              Bedrijfsnaam: *
            </label>
            <input
              type="text"
              name="companyName"
              value={formData.companyName}
              onChange={handleInputChange}
              required
              style={{
                width: "100%",
                padding: "12px",
                border: "1px solid #ddd",
                borderRadius: "5px",
                fontSize: "16px",
                boxSizing: "border-box"
              }}
              placeholder="Mijn Bedrijf B.V."
            />
          </div>
          
          <div style={{ marginBottom: "20px" }}>
            <label style={{
              display: "block",
              marginBottom: "8px",
              fontWeight: "bold",
              color: "#555"
            }}>
              KVK Nummer: *
            </label>
            <div style={{ display: "flex", gap: "10px" }}>
              <input
                type="text"
                name="kvkNumber"
                value={formData.kvkNumber}
                onChange={handleInputChange}
                required
                style={{
                  flex: 1,
                  padding: "12px",
                  border: "1px solid #ddd",
                  borderRadius: "5px",
                  fontSize: "16px",
                  boxSizing: "border-box"
                }}
                placeholder="12345678"
              />
              <button
                type="button"
                onClick={validateKvk}
                disabled={isValidatingKvk || !formData.kvkNumber.trim()}
                style={{
                  padding: "12px 20px",
                  background: isValidatingKvk ? "#ccc" : "#007bff",
                  color: "white",
                  border: "none",
                  borderRadius: "5px",
                  cursor: isValidatingKvk ? "not-allowed" : "pointer",
                  fontSize: "14px",
                  whiteSpace: "nowrap"
                }}
              >
                {isValidatingKvk ? "Controleren..." : "Controleren"}
              </button>
            </div>
            
            {/* KVK Validation Result */}
            {kvkValidation && (
              <div style={{
                marginTop: "10px",
                padding: "10px",
                borderRadius: "5px",
                background: kvkValidation.isValid ? "#d4edda" : "#f8d7da",
                border: `1px solid ${kvkValidation.isValid ? "#c3e6cb" : "#f5c6cb"}`,
                color: kvkValidation.isValid ? "#155724" : "#721c24"
              }}>
                {kvkValidation.isValid ? (
                  <div>
                    <strong>✅ KVK Nummer geverifieerd</strong>
                    <div style={{ fontSize: "12px", marginTop: "5px" }}>{kvkValidation.note}</div>
                  </div>
                ) : (
                  <div>❌ {kvkValidation.error}</div>
                )}
              </div>
            )}
          </div>

          <div style={{ marginBottom: "20px" }}>
            <label style={{
              display: "block",
              marginBottom: "8px",
              fontWeight: "bold",
              color: "#555"
            }}>
              Adres: *
            </label>
            <input
              type="text"
              name="address"
              value={formData.address}
              onChange={handleInputChange}
              required
              style={{
                width: "100%",
                padding: "12px",
                border: "1px solid #ddd",
                borderRadius: "5px",
                fontSize: "16px",
                boxSizing: "border-box"
              }}
              placeholder="Hoofdstraat 123"
            />
          </div>

          <div style={{ display: "flex", gap: "15px", marginBottom: "20px" }}>
            <div style={{ flex: 1 }}>
              <label style={{
                display: "block",
                marginBottom: "8px",
                fontWeight: "bold",
                color: "#555"
              }}>
                Postcode: *
              </label>
              <input
                type="text"
                name="postalCode"
                value={formData.postalCode}
                onChange={handleInputChange}
                required
                style={{
                  width: "100%",
                  padding: "12px",
                  border: "1px solid #ddd",
                  borderRadius: "5px",
                  fontSize: "16px",
                  boxSizing: "border-box"
                }}
                placeholder="1234 AB"
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={{
                display: "block",
                marginBottom: "8px",
                fontWeight: "bold",
                color: "#555"
              }}>
                Plaats: *
              </label>
              <input
                type="text"
                name="city"
                value={formData.city}
                onChange={handleInputChange}
                required
                style={{
                  width: "100%",
                  padding: "12px",
                  border: "1px solid #ddd",
                  borderRadius: "5px",
                  fontSize: "16px",
                  boxSizing: "border-box"
                }}
                placeholder="Amsterdam"
              />
            </div>
          </div>

          <div style={{ marginBottom: "20px" }}>
            <label style={{
              display: "block",
              marginBottom: "8px",
              fontWeight: "bold",
              color: "#555"
            }}>
              Telefoonnummer: *
            </label>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
              required
              style={{
                width: "100%",
                padding: "12px",
                border: "1px solid #ddd",
                borderRadius: "5px",
                fontSize: "16px",
                boxSizing: "border-box"
              }}
              placeholder="020 1234567"
            />
          </div>

          <div style={{ marginBottom: "20px" }}>
            <label style={{
              display: "block",
              marginBottom: "8px",
              fontWeight: "bold",
              color: "#555"
            }}>
              Website: *
            </label>
            <input
              type="url"
              name="website"
              value={formData.website}
              onChange={handleInputChange}
              required
              style={{
                width: "100%",
                padding: "12px",
                border: "1px solid #ddd",
                borderRadius: "5px",
                fontSize: "16px",
                boxSizing: "border-box"
              }}
              placeholder="https://www.mijnwinkel.nl"
            />
          </div>
          
          <div style={{ marginBottom: "20px" }}>
            <label style={{
              display: "block",
              marginBottom: "8px",
              fontWeight: "bold",
              color: "#555"
            }}>
              Email: *
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              required
              style={{
                width: "100%",
                padding: "12px",
                border: "1px solid #ddd",
                borderRadius: "5px",
                fontSize: "16px",
                boxSizing: "border-box"
              }}
              placeholder="admin@mijnwinkel.nl"
            />
          </div>
          
          <div style={{ marginBottom: "20px" }}>
            <label style={{
              display: "block",
              marginBottom: "8px",
              fontWeight: "bold",
              color: "#555"
            }}>
              Wachtwoord: *
            </label>
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              required
              style={{
                width: "100%",
                padding: "12px",
                border: "1px solid #ddd",
                borderRadius: "5px",
                fontSize: "16px",
                boxSizing: "border-box"
              }}
              placeholder="••••••••"
            />
          </div>
          
          <div style={{ marginBottom: "30px" }}>
            <label style={{
              display: "block",
              marginBottom: "8px",
              fontWeight: "bold",
              color: "#555"
            }}>
              Bevestig Wachtwoord: *
            </label>
            <input
              type="password"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleInputChange}
              required
              style={{
                width: "100%",
                padding: "12px",
                border: "1px solid #ddd",
                borderRadius: "5px",
                fontSize: "16px",
                boxSizing: "border-box"
              }}
              placeholder="••••••••"
            />
          </div>
          
          {error && (
            <div style={{
              background: "#f8d7da",
              color: "#721c24",
              padding: "10px",
              borderRadius: "5px",
              marginBottom: "20px",
              border: "1px solid #f5c6cb"
            }}>
              {error}
            </div>
          )}
          
          <button
            type="submit"
            disabled={loading || !kvkValidation?.isValid}
            style={{
              width: "100%",
              padding: "12px",
              background: loading || !kvkValidation?.isValid ? "#ccc" : "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              color: "white",
              border: "none",
              borderRadius: "5px",
              fontSize: "16px",
              fontWeight: "bold",
              cursor: loading || !kvkValidation?.isValid ? "not-allowed" : "pointer",
              transition: "opacity 0.3s"
            }}
          >
            {loading ? "Winkel Aanmaken..." : "Winkel Aanmaken"}
          </button>
        </form>

        {/* Back Button */}
        <div style={{
          marginTop: "20px",
          textAlign: "center"
        }}>
          <button
            onClick={onBack}
            style={{
              background: "none",
              border: "none",
              color: "#667eea",
              textDecoration: "underline",
              cursor: "pointer",
              fontSize: "14px"
            }}
          >
            ← Terug naar inloggen
          </button>
        </div>

        {/* Info text */}
        <div style={{
          marginTop: "20px",
          textAlign: "center",
          fontSize: "12px",
          color: "#666",
          lineHeight: "1.4"
        }}>
          <p>Door je winkel te openen ga je akkoord met onze voorwaarden.</p>
          <p>Je KVK nummer wordt gecontroleerd om je bedrijf te verifiëren.</p>
        </div>
      </div>
    </div>
  );
};

export default OpenShop; 